import { cloneDeep, sum } from 'lodash';

import {
  Appliance,
  BatteryReserve,
  BillUploadPayload,
  CellularDetails,
  CheckoutPayload,
  CostsData,
  DeviceControlGroup,
  DeviceControlGroupPayload,
  EnergyModePayload,
  EnergyModesResponse,
  EnergyUsageV2,
  Investment,
  InvestmentPayload,
  Investments,
  Invitation,
  LiveDataSummary,
  LiveWeatherData,
  Notification,
  Notifications,
  NotificationSetting,
  NotificationsTokenPayload,
  PowerUsageV2,
  Retailer,
  RetailRate,
  SavingsAPIDataV2,
  SenseSwitch,
  SetChargeCurrentPayload,
  SiteConsent,
  SiteDataToSave,
  SiteDetailData,
  SiteDeviceSchedule,
  SiteDeviceScheduleToCreate,
  SiteListData,
  SiteSolarSpongeConfigWrite,
  SiteTariff,
  SiteTariffToSave,
  SiteUser,
  SunriseSunsetData,
  Switch,
  SwitchFirmwareVersion,
  SwitchSchedule,
  SwitchScheduleToCreate,
  Tariff,
  UploadUrlPayload,
  UserData,
  Utility,
  VersionCheckPayload,
  VersionCheckResult,
} from 'clipsal-cortex-types/src/api';
import { PaginatedResponse, PaginatedResponseV2 } from 'clipsal-cortex-types/src/common/types';

import { CreateSiteConsent } from '../site/new-user-journey/consentApi';
import { createSourceDataEntry, updateSourceDataEntry } from './mutation-helpers';
import { HTTPMethod, RETRIEVE_ROUTE_MAP } from './route-map';
import chronologicalNotifications from './stubs/chronological-notifications.json';
import convertedRetailRate from './stubs/retail_rate_converted.json';
import retailRates from './stubs/retail_rates.json';
import sites from './stubs/sites.json';
import utilities from './stubs/utilities.json';
import { getCostsData, getEnergyUsageData, getPowerUsageData, getSavingsData } from './time-series-helpers';

type OperatorPayloadTypes = Investment &
  SwitchSchedule &
  Partial<Switch> &
  SetChargeCurrentPayload &
  SiteTariff &
  SiteDeviceScheduleToCreate[] &
  SiteDeviceSchedule[] &
  SiteSolarSpongeConfigWrite &
  NotificationSetting &
  SiteDataToSave &
  SenseSwitch &
  Appliance &
  CreateSiteConsent &
  DeviceControlGroupPayload &
  EnergyModePayload;

export type OtherPayloadTypes =
  | NotificationsTokenPayload
  | Partial<SwitchSchedule>
  | SwitchScheduleToCreate
  | InvestmentPayload
  | BillUploadPayload
  | CheckoutPayload
  | VersionCheckPayload
  | UploadUrlPayload
  | SiteSolarSpongeConfigWrite;

export type AllResponseTypes =
  | Appliance[]
  | BatteryReserve
  | CostsData[]
  | EnergyModesResponse
  | EnergyUsageV2[]
  | Investments
  | Invitation[]
  | LiveDataSummary
  | LiveWeatherData
  | Notification
  | NotificationSetting[]
  | Notifications
  | PaginatedResponse<SiteListData>
  | PaginatedResponseV2<DeviceControlGroup>
  | PaginatedResponseV2<RetailRate>
  | PaginatedResponseV2<SiteConsent>
  | PaginatedResponseV2<SiteTariff>
  | PaginatedResponseV2<SiteTariffToSave>
  | PaginatedResponseV2<SiteUser>
  | PowerUsageV2[]
  | Partial<Switch>
  | Retailer
  | SavingsAPIDataV2[]
  | SenseSwitch[]
  | SiteDataToSave
  | SiteDetailData
  | SiteTariff
  | SunriseSunsetData
  | SwitchFirmwareVersion
  | SwitchSchedule
  | SwitchSchedule[]
  | Tariff
  | UserData
  | Utility[]
  | VersionCheckResult
  | { cellular_modem: CellularDetails };

const RETRIEVE_ROUTE_NAME_TO_PK_PROPERTY_NAME: Record<string, string> = {
  '/v1/switches/1/schedules': 'schedule_id',
  '/v1/switches/2/schedules': 'schedule_id',
};

type Operator = (queryParams: URLSearchParams, route: string) => AllResponseTypes | undefined;

type OperatorWithBody = (
  queryParams: URLSearchParams,
  route: string,
  body: OperatorPayloadTypes
) => AllResponseTypes | undefined;

type DynamicRouteMatchConfigurations = Record<
  HTTPMethod,
  {
    regex: RegExp;
    operator?: Operator;
    operatorWithBody?: OperatorWithBody;
  }[]
>;

const DYNAMIC_ROUTE_MATCH_CONFIGURATIONS: DynamicRouteMatchConfigurations = {
  GET: [
    {
      regex: /\/v1\/sites\?limit=\d+&offset=\d+/,
      operator: (_: URLSearchParams, __: string) => sites as PaginatedResponse<SiteListData>,
    },
    {
      regex: /\/v1\/sites\/\d+\/savings/,
      operator: getSavingsData,
    },
    {
      regex: /\/v1\/sites\/\d+\/costs/,
      operator: getCostsData,
    },
    {
      regex: /\/v1\/sites\/\d+\/power_usage/,
      operator: getPowerUsageData,
    },
    {
      regex: /\/v1\/sites\/\d+\/energy_usage/,
      operator: getEnergyUsageData,
    },
    {
      regex: /\/v1\/wattbuy\/utilities(.*)/,
      operator: (_: URLSearchParams, __: string) => utilities as Utility[],
    },
    {
      regex: /\/v1\/wattbuy\/retail_rates(.*)/,
      operator: (_: URLSearchParams, __: string) => retailRates as unknown as PaginatedResponseV2<RetailRate>,
    },
    {
      regex: /\/v1\/sites\/\d+\/energy_usage/,
      operator: getEnergyUsageData,
    },
    {
      regex: /\/v1\/sites\/\d+\/sunrise_sunset/,
      operator: (queryParams: URLSearchParams, __: string) => {
        // get the date from the query params, or use today's date if not provided
        const formattedDate = queryParams.get('date') ?? new Date().toISOString().split('T')[0];

        return {
          sunrise: `${formattedDate} 06:10:58`,
          sunset: `${formattedDate} 20:32:33`,
        };
      },
    },
    {
      regex: /\/v1\/notifications\/[0-9A-Z?=_&%-]+/,
      operator: (_: URLSearchParams, route: string) => {
        const id = /\/v1\/notifications\/(\w+)/.exec(route);
        return chronologicalNotifications.results.find((r) => r.notification_id === id![1]) as Notification;
      },
    },
  ],
  PATCH: [
    {
      regex: /\/v1\/user\/notification_settings\/(\d+)/,
      operatorWithBody: (_: URLSearchParams, route: string, body: NotificationSetting) => {
        const match = /\/v1\/user\/notification_settings\/(\d+)/.exec(route);
        const id = match![1];
        return updateSourceDataEntry(
          '/v1/user/notification_settings?related_app=Schneider Home',
          body,
          (source: NotificationSetting[]) => {
            return source.map((n) => (n['id'] === Number(id) ? { ...n, ...body } : n));
          }
        );
      },
    },
    {
      regex: /\/v1\/sites\/(\d+)\/energy_mode/,
      operatorWithBody: (_: URLSearchParams, __: string, body: EnergyModePayload) => {
        return updateSourceDataEntry('/v1/sites/1/energy_mode', body);
      },
    },
    {
      regex: /\/v1\/sites\/(\d+)/,
      operatorWithBody: (_: URLSearchParams, __: string, body: SiteDataToSave) => {
        return updateSourceDataEntry('/v1/sites/1', body);
      },
    },
    {
      regex: /\/v1\/sense\/switches\/(\d+)/,
      operatorWithBody: (_: URLSearchParams, route: string, body: SenseSwitch) => {
        const match = /\/v1\/sense\/switches\/(\d+)/.exec(route);
        const id = match![1];
        return updateSourceDataEntry('/v1/sense/sites/1/switches', body, (source: SenseSwitch[]) => {
          return source.map((n) => (n['id'] === Number(id) ? { ...n, ...body } : n));
        });
      },
    },
    {
      regex: /\/v1\/schedules\/(\d+)/,
      operatorWithBody: (_: URLSearchParams, route: string, body: SwitchSchedule) => {
        const match = /\/v1\/schedules\/(\d+)/.exec(route);
        const id = match![1];
        // Unfortunate bit of duplication here, but it's the only way to get the correct route.
        const retrieveRouteMatchingSchedule = ['/v1/switches/1/schedules', '/v1/switches/2/schedules'].find(
          (scheduleRoute) => {
            return (RETRIEVE_ROUTE_MAP[scheduleRoute] as SwitchSchedule[]).find(
              (savedSchedule: SwitchSchedule) => savedSchedule.schedule_id === Number(id)
            );
          }
        );

        if (!retrieveRouteMatchingSchedule)
          throw new Error(`Schedule with ID ${id} not found in any stored stubbed GET route entry`);

        return updateSourceDataEntry(retrieveRouteMatchingSchedule, body, (source: SwitchSchedule[]) => {
          return source.map((n) => (n['schedule_id'] === Number(id) ? { ...n, ...body } : n));
        });
      },
    },
    {
      regex: /\/v1\/sites\/1\/appliances\/(\d+)/,
      operatorWithBody: (_: URLSearchParams, route: string, body: Appliance) => {
        const match = /\/v1\/sites\/1\/appliances\/(\d+)/.exec(route);
        const id = match![1];
        return updateSourceDataEntry('/v1/sites/1/appliances', body, (source: Appliance[]) => {
          return source.map((n) => (n['appliance_id'] === Number(id) ? { ...n, ...body } : n));
        });
      },
    },
    {
      regex: /\/v1\/sites\/1\/investments\/(\d+)/,
      operatorWithBody: (_: URLSearchParams, route: string, body: Investment) => {
        const match = /\/v1\/sites\/1\/investments\/(\d+)/.exec(route);
        const investmentId = Number(match![1]);

        return updateSourceDataEntry('/v1/sites/1/investments', [body], (source: Investments) => {
          const updatedInvestments = source.investments.map((investment: Investment) => {
            if (investment.id === investmentId) {
              return {
                ...investment,
                ...body,
              };
            }
            return investment;
          });
          const updatedTotalInvestment = sum(updatedInvestments.map((investment: Investment) => investment.amount));
          const updatedPaybackRemaining = updatedTotalInvestment - source.total_savings;
          return {
            ...source,
            investments: updatedInvestments,
            total_investment: updatedTotalInvestment,
            payback_remaining: updatedPaybackRemaining,
          };
        });
      },
    },
    {
      regex: /\/v1\/site_devices\/\d+\/battery_reserve/,
      operatorWithBody: (_: URLSearchParams, __: string, body) =>
        updateSourceDataEntry('/v1/site_devices/2/battery_reserve', body),
    },
    {
      regex: /\/v1\/user/,
      operatorWithBody: (_: URLSearchParams, __: string, body) => updateSourceDataEntry('/v1/user', body),
    },
  ],
  DELETE: [
    {
      regex: /\/v1\/schedules\/(\d+)/,
      operatorWithBody: (_: URLSearchParams, route: string, body: Partial<SwitchSchedule>) => {
        const match = /\/v1\/schedules\/(\d+)/.exec(route);
        const scheduleId = Number(match![1]);
        const source = RETRIEVE_ROUTE_MAP[`/v1/switches/${body.switch_id}/schedules`];
        const schedules = cloneDeep(source) as SwitchSchedule[];
        RETRIEVE_ROUTE_MAP[`/v1/switches/${body.switch_id}/schedules`] = schedules.filter(
          (schedule) => schedule.schedule_id !== scheduleId
        );
        return {};
      },
    },
    {
      regex: /\/v1\/controls\/\d+\/groups\/(\d+)/,
      operator: (_: URLSearchParams, route: string) => {
        const match = /\/v1\/controls\/(\d+)\/groups\/(\d+)/.exec(route);
        const switchId = Number(match![1]);
        const groupId = Number(match![2]);
        const retrieveSwitchGroups = RETRIEVE_ROUTE_MAP[
          `/v1/sites/1/groups`
        ] as PaginatedResponseV2<DeviceControlGroup>;

        return updateSourceDataEntry(`/v1/sites/1/groups`, {
          ...retrieveSwitchGroups,
          results: retrieveSwitchGroups.results.map((group: DeviceControlGroup) => {
            if (group.id === groupId) {
              return {
                ...group,
                control_devices: [
                  ...group.control_devices.filter((switchGroupDevice) => switchGroupDevice.id !== switchId),
                ],
              };
            } else return group;
          }),
        });
      },
    },
    {
      regex: /\/v2\/site_tariffs\/(\d+)/,
      operator: (_, route: string) => {
        const match = /\/v2\/site_tariffs\/(\d+)/.exec(route);
        const tariffId = Number(match![1]);
        const source = RETRIEVE_ROUTE_MAP['/v2/sites/1/tariffs'] as PaginatedResponseV2<SiteTariffToSave>;
        const clonedSource = cloneDeep(source);
        clonedSource.results = (clonedSource.results as SiteTariff[]).filter((tariff) => tariff.id !== tariffId);
        RETRIEVE_ROUTE_MAP['/v2/sites/1/tariffs'] = clonedSource;
        return {};
      },
    },
  ],
  POST: [
    {
      regex: /\/v1\/version_check/,
      operator: (_: URLSearchParams, __: string) => ({
        update_required: false,
        title: 'No Update Required',
        message: null,
      }),
    },
    {
      regex: /\/v1\/controls\/\d+\/groups/,
      operatorWithBody: (_: URLSearchParams, route: string, body: DeviceControlGroupPayload) => {
        const match = /\/v1\/controls\/(\d+)\/groups/.exec(route);
        const switchId = Number(match![1]);
        const groupId = body.group;
        const retrieveSwitchGroups = RETRIEVE_ROUTE_MAP[
          `/v1/sites/1/groups`
        ] as PaginatedResponseV2<DeviceControlGroup>;
        const retrieveSwitches = RETRIEVE_ROUTE_MAP[`/v1/sense/sites/1/switches`] as SenseSwitch[];

        return updateSourceDataEntry(`/v1/sites/1/groups`, {
          ...retrieveSwitchGroups,
          results: retrieveSwitchGroups.results.map((group: DeviceControlGroup) => {
            if (group.id === groupId) {
              return {
                ...group,
                control_devices: [
                  ...group.control_devices,
                  {
                    group: groupId,
                    id: switchId,
                    group_name: group.group_name,
                    control_device: { ...retrieveSwitches.find((switchItem) => switchItem.id === switchId)! },
                  },
                ],
              };
            } else return group;
          }),
        });
      },
    },
    {
      regex: /\/v1\/wattbuy\/utilities\/\d+\/convert$/,
      operator: (_: URLSearchParams, __: string) => ({
        id: 1,
        name: 'PG&E',
        url: 'https://agl.com.au',
        country_code: 'AU',
      }),
    },
    {
      regex: /\/v1\/wattbuy\/retail_rates\/[a-z0-9]+\/convert$/,
      operator: (_: URLSearchParams, __: string) => convertedRetailRate as Tariff,
    },
    {
      regex: /\/v2\/sites\/\d+\/tariffs/,
      operatorWithBody: (_, route: string, body: SiteTariff) => {
        const newTariff = updateTariffBodyToMatchAPISchema(body);
        const source = RETRIEVE_ROUTE_MAP[route] as PaginatedResponseV2<SiteTariff>;
        const clonedSource = cloneDeep(source);
        clonedSource.results.push(newTariff);
        RETRIEVE_ROUTE_MAP[route] = clonedSource;
        return newTariff;
      },
    },
    {
      regex: /\/v1\/sites\/1\/investments/,
      operatorWithBody: (_: URLSearchParams, __: string, body: Investment) => {
        return updateSourceDataEntry('/v1/sites/1/investments', [body], (source: Investments) => {
          const updatedInvestments = [
            ...source.investments,
            { ...body, id: source.investments[source.investments.length - 1].id + 1 },
          ];
          const updatedTotalInvestment = sum(updatedInvestments.map((investment: Investment) => investment.amount));
          const updatedPaybackRemaining = updatedTotalInvestment - source.total_savings;
          return {
            ...source,
            investments: updatedInvestments,
            total_investment: updatedTotalInvestment,
            payback_remaining: updatedPaybackRemaining,
          };
        });
      },
    },
  ],
  PUT: [
    {
      regex: /\/v1\/sites\/1\/consent\/(\d+)/,
      operatorWithBody: (_: URLSearchParams, route: string, body: CreateSiteConsent) => {
        const match = /\/v1\/sites\/(\d+)\/consent\/(\d+)/.exec(route);
        const consentId = Number(match![2]);
        return updateSourceDataEntry(
          '/v1/sites/1/consent',
          { results: [{ ...body, id: consentId, consenting_user: 1 }] },
          (source: PaginatedResponseV2<SiteConsent>) => {
            return {
              ...source,
              results: [source.results.find((n) => n.id === Number(consentId)) as SiteConsent],
            };
          }
        );
      },
    },
    {
      regex: /\/v2\/site_tariffs\/\d+/,
      operatorWithBody: (_, __: string, body: SiteTariff) => {
        const updatedTariff = updateTariffBodyToMatchAPISchema(body);
        const source = RETRIEVE_ROUTE_MAP['/v2/sites/1/tariffs'] as PaginatedResponseV2<SiteTariff>;
        const clonedSource = cloneDeep(source);
        clonedSource.results = clonedSource.results.map((tariff) => {
          if (tariff.id === updatedTariff.id) return updatedTariff;
          return tariff;
        });
        RETRIEVE_ROUTE_MAP['/v2/sites/1/tariffs'] = clonedSource;
        return updatedTariff;
      },
    },
  ],
};

function updateTariffBodyToMatchAPISchema(body: SiteTariff): SiteTariff {
  const tariffData = body;
  const currentTariffList = (RETRIEVE_ROUTE_MAP['/v2/sites/1/tariffs'] as PaginatedResponseV2<SiteTariff>).results;
  const isNewTariff = !tariffData.id;
  const id = isNewTariff ? currentTariffList.length + 1 : tariffData.id;
  return {
    ...tariffData,
    id,
    tariff: {
      id,
      ...tariffData.tariff,
      rates: tariffData.tariff.rates.map((rate, rateIndex) => ({
        ...rate,
        id: rateIndex + 1,
        season: tariffData.tariff.seasons?.find((season) => season.season_index === rate.season_index) ?? null,
      })),
    },
  };
}

/**
 * Some routes require data retrieved dynamically (mostly the usage page), they're returned through this helper.
 *
 * @param method - The HTTP method of the request.
 * @param route - The route to check.
 * @param body - [optional] The body of a PUT/PATCH/POST request, as an object.
 * @returns The appropriate JSON data as an object/array, or false if not applicable.
 */
export function getDynamicStubDataForRoute(method: HTTPMethod, route: string, body?: OtherPayloadTypes) {
  const queryParams = new URLSearchParams(new URL('http://example.com' + route).search);

  try {
    const matchedDynamicResponse = DYNAMIC_ROUTE_MATCH_CONFIGURATIONS[method].find(({ regex }) => regex.exec(route));
    if (matchedDynamicResponse) {
      if (matchedDynamicResponse.operatorWithBody && body !== undefined) {
        return matchedDynamicResponse.operatorWithBody(queryParams, route, body as OperatorPayloadTypes);
      }
      if (matchedDynamicResponse.operator) {
        return matchedDynamicResponse.operator(queryParams, route);
      }
    }
  } catch (e) {
    console.error(e);
    return false;
  }

  if (method === 'POST') {
    try {
      // POST endpoints usually have the same route as their GET counterpart, so we rely on this
      return createSourceDataEntry(route, body, RETRIEVE_ROUTE_NAME_TO_PK_PROPERTY_NAME[route]);
    } catch (e) {
      console.error(e);
      return false;
    }
  }
  return false;
}
